import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, Typography, Button, MenuItem, TextField, CircularProgress } from '@material-ui/core';
import { darken } from '@material-ui/core/styles/colorManipulator';
import { makeStyles } from '@material-ui/styles';
import clsx from 'clsx';
import { useDispatch } from 'react-redux';
import history from '@history';
import jwtService from 'app/services/jwtService';
import * as authActions from 'app/auth/store/actions';
import * as Actions from 'app/store/actions';
import { clearStoredGoogleOAuthState, getStoredGoogleOAuthState } from './googleOAuth';

const useStyles = makeStyles(theme => ({
    root: {
        background: 'linear-gradient(to right, ' + theme.palette.primary.dark + ' 0%, ' + darken(theme.palette.primary.dark, 0.5) + ' 100%)',
        color: theme.palette.primary.contrastText
    }
}));

function LoginCallback() {
    const classes = useStyles();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [email, setEmail] = useState('');
    const [empresas, setEmpresas] = useState([]);
    const [empresaId, setEmpresaId] = useState('');
    const [perfiles, setPerfiles] = useState([]);
    const [perfil, setPerfil] = useState('');
    const [needsProfile, setNeedsProfile] = useState(false);
    const [preauthToken, setPreauthToken] = useState('');

    const message = useCallback((type = 'null', messageText = '') => {
        dispatch(
            Actions.showMessage({
                message: messageText,
                autoHideDuration: 6000,
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'center'
                },
                variant: type
            })
        );
    }, [dispatch]);

    // Paso final: empresa ya elegida (y perfil, si hacía falta). Si el
    // backend todavía no puede loguear porque hay más de un perfil en esa
    // empresa, muestra el selector de perfil en vez de terminar.
    const finalizeLogin = useCallback(async (token, companyId, selectedPerfil) => {
        setSubmitting(true);

        try {
            const result = await jwtService.finalizeGoogleLogin(token, companyId, selectedPerfil);

            if (result.requiresProfile) {
                const disponibles = result.perfiles || [];
                setPerfiles(disponibles);
                setPerfil(disponibles[0]?.profile_slug || '');
                setNeedsProfile(true);
                setLoading(false);
                setSubmitting(false);
                return;
            }

            dispatch(authActions.setUserData(result.user));
            dispatch({ type: authActions.LOGIN_SUCCESS });
            history.replace('/');
        } catch (finalizeError) {
            const errorMessage = finalizeError || 'No se pudo finalizar el login con Google';
            setError(errorMessage);
            setLoading(false);
            message('error', errorMessage);
            setSubmitting(false);
        }
    }, [dispatch, message]);

    useEffect(() => {
        let mounted = true;

        async function handleGoogleCallback() {
            const params = new URLSearchParams(window.location.search);
            const code = params.get('code');
            const state = params.get('state');
            const googleError = params.get('error');
            const googleErrorDescription = params.get('error_description');
            const storedState = getStoredGoogleOAuthState();

            if (googleError) {
                const errorMessage = googleErrorDescription || googleError;
                clearStoredGoogleOAuthState();
                if (mounted) {
                    setError(errorMessage);
                    setLoading(false);
                    message('error', errorMessage);
                }
                return;
            }

            if (!code || !state || !storedState || storedState !== state) {
                clearStoredGoogleOAuthState();
                const errorMessage = 'El callback de Google es inválido o expiró';
                if (mounted) {
                    setError(errorMessage);
                    setLoading(false);
                    message('error', errorMessage);
                }
                return;
            }

            clearStoredGoogleOAuthState();

            try {
                const response = await jwtService.exchangeGoogleCode(code, state);

                if (!mounted) {
                    return;
                }

                const availableEmpresas = Array.isArray(response.empresas) ? response.empresas : [];

                setEmail(response.email);
                setEmpresas(availableEmpresas);
                setPreauthToken(response.preauth_token);

                if (availableEmpresas.length === 1) {
                    setEmpresaId(availableEmpresas[0].id);
                    await finalizeLogin(response.preauth_token, availableEmpresas[0].id);
                    return;
                }

                setLoading(false);
            } catch (exchangeError) {
                const errorMessage = exchangeError.response?.data?.message || exchangeError.message || 'No se pudo validar el acceso con Google';
                if (mounted) {
                    setError(errorMessage);
                    setLoading(false);
                    message('error', errorMessage);
                }
            }
        }

        handleGoogleCallback();

        return () => {
            mounted = false;
        };
    }, [finalizeLogin, message]);

    function goBackToLogin() {
        history.replace('/login');
    }

    return (
        <div className={clsx(classes.root, 'flex flex-col flex-1 items-center justify-center p-24')}>
            <Card className="w-full max-w-400 mx-auto" square>
                <CardContent className="flex flex-col items-center justify-center p-32 md:p-48">
                    <Typography variant="h6" className="text-center md:w-full mb-32">
                        Acceso con Google
                    </Typography>

                    {loading ? (
                        <div className="flex flex-col items-center justify-center">
                            <CircularProgress />
                            <Typography className="mt-16" align="center">
                                Validando autenticación...
                            </Typography>
                        </div>
                    ) : error ? (
                        <div className="w-full text-center">
                            <Typography color="error" className="mb-24">
                                {error}
                            </Typography>
                            <Button
                                variant="contained"
                                color="primary"
                                className="normal-case"
                                onClick={goBackToLogin}
                            >
                                Volver al login
                            </Button>
                        </div>
                    ) : needsProfile ? (
                        <div className="w-full">
                            <TextField
                                className="mb-16"
                                label="Email"
                                value={email}
                                variant="outlined"
                                fullWidth
                                disabled
                            />

                            <TextField
                                select
                                className="mb-16"
                                label="Seleccione un perfil"
                                value={perfil}
                                onChange={(event) => setPerfil(event.target.value)}
                                variant="outlined"
                                fullWidth
                            >
                                {perfiles.map((item) => (
                                    <MenuItem key={item.profile_slug} value={item.profile_slug}>
                                        {item.profile_name}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <Button
                                variant="contained"
                                color="primary"
                                className="w-full mx-auto mt-16 normal-case"
                                disabled={!perfil || submitting}
                                onClick={() => finalizeLogin(preauthToken, empresaId, perfil)}
                            >
                                {submitting ? 'Ingresando...' : 'Continuar'}
                            </Button>
                        </div>
                    ) : (
                        <div className="w-full">
                            <TextField
                                className="mb-16"
                                label="Email"
                                value={email}
                                variant="outlined"
                                fullWidth
                                disabled
                            />

                            <TextField
                                select
                                className="mb-16"
                                label="Seleccione una empresa"
                                value={empresaId}
                                onChange={(event) => setEmpresaId(event.target.value)}
                                variant="outlined"
                                fullWidth
                            >
                                {empresas.map((item) => (
                                    <MenuItem key={item.id} value={item.id}>
                                        {item.razon_social}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <Button
                                variant="contained"
                                color="primary"
                                className="w-full mx-auto mt-16 normal-case"
                                disabled={!empresaId || submitting}
                                onClick={() => finalizeLogin(preauthToken, empresaId)}
                            >
                                {submitting ? 'Ingresando...' : 'Continuar'}
                            </Button>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}

export default LoginCallback;
